// @flow

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectOneProduct,
  selectProductsByTypeAndProcess,
  type ProductData,
} from '@gandi/cart';
import type { Fqdn } from '@gandi/domain-configuration';
import type { TranslatorProps } from '@gandi/translate';

import type { ThunkAction } from '../../types';
import type { BulkProducts } from '../types';

import type { ServicePackage, Service } from '../types/marketplace';

import {
  fetchDomainService,
  fetchDomainServices,
  getCurrentServicePackage,
  selectInstalledServices,
} from './marketplace';

// copied from app-domain/src/Domain/Detail/Marketplace/modules/consts.js
const MAX_SERVICE_DURATION = 12 as const;

const getServiceIdentifier = (fqdn: string, serviceId: string) =>
  `${fqdn}/services/${serviceId}`;

const getDomainServicesToAdd =
  (
    translator: TranslatorProps,
    fqdn: Fqdn,
    minDuration: number,
  ): ThunkAction<Promise<Array<ProductData>>> =>
  async (dispatch, getState): Promise<Array<ProductData>> => {
    try {
      await dispatch(fetchDomainServices(fqdn));
    } catch (e) {
      return [];
    }

    // we should have only grab installed services from the api
    const services: Array<Service> = selectInstalledServices(getState(), fqdn);

    if (services.length) {
      // $FlowFixMe does Promise.all really return Array<ProductData> here?
      const servicesProducts: Array<ProductData> = await Promise.all(
        services.map(async service => {
          try {
            // fetch full details of the service
            // which adding `packages` and `contracts`
            // maybe we could optimize the 1st api call
            // TODO: ask to api team
            const domainService = await dispatch(
              fetchDomainService(fqdn, service.service_id),
            );

            const currentPkg: ?ServicePackage = getCurrentServicePackage(
              domainService.data,
            );

            if (!currentPkg || currentPkg.free || currentPkg.trial) {
              return false;
            }

            return {
              product_type: 'marketplace',
              process: 'renew',
              payload: {
                duration: minDuration * MAX_SERVICE_DURATION,
                duration_unit: 'm',
                label: translator.__('Renew %(service)s %(pkg)s', {
                  service: service.name_label,
                  pkg: currentPkg.name_label,
                }),
                fqdn,
              },
              identifier: getServiceIdentifier(fqdn, service.service_id),
            };
          } catch (e) {
            return false;
          }
        }),
      );

      return servicesProducts.filter(Boolean);
    }

    return [];
  };

// getServicesBulkData
export const appendServicesBulkData =
  (
    translator: TranslatorProps,
    bulkProducts: BulkProducts,
    checkRenewalInTheCart?: boolean,
  ): ThunkAction<Promise<BulkProducts>> =>
  async (dispatch, getState) => {
    // as marketplace/services is not anonymous api
    // we should check if the user is connected
    if (!selectIsAuthenticatedUser(getState())) {
      return bulkProducts;
    }

    // grab related services
    const servicePromises = [];

    bulkProducts.data.forEach(product => {
      servicePromises.push(
        dispatch(
          getDomainServicesToAdd(
            translator,
            // $FlowFixMe[incompatible-call]
            product.identifier, // should be eq to `fqdn`
            product.payload?.duration || 1,
          ),
        ),
      );
    });

    // add services related to products ready in the cart
    if (checkRenewalInTheCart) {
      const renewalsInCart = selectProductsByTypeAndProcess(getState(), {
        productType: 'domain',
        process: 'renew',
      });

      renewalsInCart.forEach(product =>
        servicePromises.push(
          dispatch(
            getDomainServicesToAdd(
              translator,
              // $FlowFixMe[incompatible-call]
              product.identifier, // should be eq to `fqdn`
              product.payload?.duration || 1,
            ),
          ),
        ),
      );
    }

    // eslint-disable-next-line ft-flow/no-weak-types
    let serviceResults: Array<Object> = [];
    try {
      serviceResults = await Promise.allSettled(servicePromises);
    } catch (e) {
      // fail silently
    }

    return serviceResults
      .map(result => result.value) // get the returned value of each promise
      .filter(value => value?.length) // remove the ones without any services or `value`
      .flatMap(a => a) // put all services of each domain at same level (eq `Array.flat()`)
      .reduce(
        // feed the bulk with services
        (acc, current: ProductData) => {
          // check the service is not already the cart
          if (
            !selectOneProduct(getState(), { identifier: current.identifier })
          ) {
            // append the service
            acc.data.push(current);
            // $FlowFixMe refine @gandi/cart Product
            acc.extra[current.identifier] = {
              // $FlowFixMe[incompatible-type]
              label: current.identifier,
              // $FlowFixMe[incompatible-type]
              identifier: current.identifier,
            };
          }
          return acc;
        },
        bulkProducts,
      );
  };
